import { ApolloClient, ApolloLink, DefaultContext, from, InMemoryCache, NormalizedCacheObject } from '@apollo/client'
import { ErrorResponse, onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'
import { getInStorage, TOKEN_NAME } from '../token'
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs'
import { GraphQLFormattedError } from 'graphql/error'
import Bugsnag from '@bugsnag/js'

// En dev (npm run dev), VITE_API_URL pointe vers localhost. Si le front est chargé depuis un autre
// hôte (ex: IP LAN du Mac depuis un iPhone), on reprend ce même hôte pour l'API afin que ça marche
// aussi bien en local que depuis un autre appareil du réseau, sans avoir à changer le .env.
function resolveApiUrl(): string {
    const configured: string = import.meta.env.VITE_API_URL || 'http://cald.localhost/api-v2/'

    try {
        const url: URL = new URL(configured)
        const isLocalhost: boolean = url.hostname === 'localhost' || url.hostname === '127.0.0.1'

        if (isLocalhost && typeof window !== 'undefined') {
            url.hostname = window.location.hostname
        }

        return url.toString()
    } catch {
        return configured
    }
}

const httpLink: ApolloLink = createUploadLink({
    uri: resolveApiUrl(),
    headers: { 'Apollo-Require-Preflight': 'true' }
})

const errorLink: ApolloLink = onError(({ graphQLErrors, networkError }: ErrorResponse): void => {
    if (graphQLErrors)
        graphQLErrors.forEach(({ message, locations, path }: GraphQLFormattedError): void => {
            Bugsnag.notify(new Error(`[GraphQL error]: ${message}`), (event) => {
                event.addMetadata('graphql', { locations, path })
            })
        })

    if (networkError) Bugsnag.notify(networkError)
})

const authLink: ApolloLink = setContext(async (_, { headers }: DefaultContext) => {
    const token: string = await getInStorage(TOKEN_NAME)
    return {
        headers: {
            ...headers,
            authorization: token ? token : ''
        }
    }
})

const client: ApolloClient<NormalizedCacheObject> = new ApolloClient({
    link: from([errorLink, authLink, httpLink]),
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'no-cache'
        },
        mutate: {
            fetchPolicy: 'no-cache'
        },
        query: {
            fetchPolicy: 'no-cache'
        }
    },
    ssrMode: false,
    cache: new InMemoryCache(),
    queryDeduplication: true,
    clientAwareness: {
        name: 'web',
        version: __APP_VERSION__
    }
})

export default client
